"use client"

import React, { useEffect, useState } from "react"
import quizData from "../documents.json"
import { QuizQuestion } from "./QuizQuestion"
import { QuizResult } from "./QuizResult"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

interface DocumentData {
  書類名: string
  届出先: string
  届出者: string
  届出時期: string
  補足事項: string
  タイプ: string
}

interface AnswerRecord {
  docName: string
  userAnswers: {
    todokedesaki: string
    todokedesha: string
    todokejiki: string
  }
  correctAnswers: {
    todokedesaki: string
    todokedesha: string
    todokejiki: string
  }
  isCorrectObj: {
    todokedesaki: boolean
    todokedesha: boolean
    todokejiki: boolean
  }
  hasNote: boolean
  note: string
}

export default function DocumentsQuiz() {
  const [data, setData] = useState<DocumentData[]>([])

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFinished, setIsFinished] = useState(false)
  const [isRevealed, setIsRevealed] = useState(false)

  const [selectedSaki, setSelectedSaki] = useState("")
  const [selectedSha, setSelectedSha] = useState("")
  const [selectedJiki, setSelectedJiki] = useState("")

  const [answers, setAnswers] = useState<AnswerRecord[]>([])

  // 初回マウントでデータをシャッフル
  useEffect(() => {
    const cloned = [...quizData]
    shuffleArray(cloned)
    setData(cloned)
  }, [])

  if (!data || data.length === 0) {
    return <p>読み込み中...</p>
  }

  // 全問終了
  if (isFinished) {
    return (
      <QuizResult
        answerRecords={answers}
        onReset={() => {
          // リセット
          const cloned = [...quizData]
          shuffleArray(cloned)
          setData(cloned)
          setCurrentIndex(0)
          setIsFinished(false)
          setIsRevealed(false)
          setAnswers([])
          setSelectedSaki("")
          setSelectedSha("")
          setSelectedJiki("")
        }}
      />
    )
  }

  const currentDoc = data[currentIndex]
  const hasNote = currentDoc.補足事項 !== ""

  /** 回答を見る */
  const handleReveal = () => {
    setIsRevealed(true)
  }

  /** 次へ */
  const handleNext = () => {
    const correct = {
      todokedesaki: currentDoc.届出先,
      todokedesha: currentDoc.届出者,
      todokejiki: currentDoc.届出時期,
    }
    const user = {
      todokedesaki: selectedSaki,
      todokedesha: selectedSha,
      todokejiki: selectedJiki,
    }
    const check = {
      todokedesaki: user.todokedesaki === correct.todokedesaki,
      todokedesha: user.todokedesha === correct.todokedesha,
      todokejiki: user.todokejiki === correct.todokejiki,
    }

    setAnswers((prev) => [
      ...prev,
      {
        docName: currentDoc.書類名,
        userAnswers: user,
        correctAnswers: correct,
        isCorrectObj: check,
        hasNote,
        note: currentDoc.補足事項,
      },
    ])

    const nextIndex = currentIndex + 1
    if (nextIndex < data.length) {
      setCurrentIndex(nextIndex)
      setSelectedSaki("")
      setSelectedSha("")
      setSelectedJiki("")
      setIsRevealed(false)
    } else {
      setIsFinished(true)
    }
  }

  return (
    <Card className="max-w-2xl mx-auto my-8">
      <CardHeader>
        <CardTitle>
          問題 {currentIndex + 1} / {data.length}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <QuizQuestion
          documentData={currentDoc}
          allDocs={data}
          isRevealed={isRevealed}
          selectedSaki={selectedSaki}
          setSelectedSaki={setSelectedSaki}
          selectedSha={selectedSha}
          setSelectedSha={setSelectedSha}
          selectedJiki={selectedJiki}
          setSelectedJiki={setSelectedJiki}
        />

        {/* 補足事項のためのスペース */}
        <div className="min-h-[60px] flex items-center mb-4">
          {isRevealed && hasNote && (
            <div className="flex items-start gap-2 text-sm">
              {/* lucideアイコン例 */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-none text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.158 4.518l-7.111 12.45A2 2 0 003.06 20h17.88a2 2 0 001.838-2.683l-7.112-12.45a2 2 0 00-3.608 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01"
                />
              </svg>
              <p className="text-blue-700 whitespace-pre-wrap">
                {currentDoc.補足事項}
              </p>
            </div>
          )}
        </div>

        {!isRevealed ? (
          <Button onClick={handleReveal}>回答を見る</Button>
        ) : (
          <Button onClick={handleNext}>次へ</Button>
        )}
      </CardContent>
    </Card>
  )
}

function shuffleArray<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const r = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[r]] = [arr[r], arr[i]]
  }
}
